import { Component, Prop, Vue } from 'vue-property-decorator';
import { mapState, mapGetters } from 'vuex';
import moment from 'moment';
import _ from 'lodash';

import { EntityOep, Process, ProcessConfigFactory, ProcessConfigGroup, ProcessConfigPersonnelType, VacanciesAttached } from '@/Domain/Entities';
import { ProcessType, ProcessPhases, OppositionType, Languages, ProcessStatus, Constants, ProcessConfigurationTypes } from '@/Domain/enum';
import { MdDatepicker, MdTextarea, MdSelect, Toggle, MdRadio } from '@/Components';

import EntityService from '@/Services/EntityService';
import MasterDataService from '@/Services/MasterDataService';
import { convertStringToBigInt } from '@/utils';

@Component({
    components: {
        MdTextarea,
        MdSelect,
        MdDatepicker,
        MdRadio,
        Toggle
    },
    computed: {
        ...mapState('featureFlagsStore', ['multiLanguage', 'preferenceForThePlace']),
        ...mapState('languagesStore', { currentLanguage: 'current' }),
        ...mapGetters('languagesStore', { availableLanguages: 'getTranslatableLanguages' }),
        ...mapGetters('processConfigStore', { processConfigPersonnelTypes: 'getProcessConfigPersonnelTypes' }),
        isAttachMeritsValid() {
            if (this.$props.value.meritPresentationStartDate && this.$props.value.meritPresentationEndDate) {
                return this.$props.value.meritPresentationStartDate.isBefore(this.$props.value.meritPresentationEndDate);
            }
            return true;
        },
        isAttachMeritsPeriodAfterClaimsEndDateOrEndDate() {
            if (this.$props.value.meritPresentationStartDate && this.$props.value.meritPresentationEndDate && !this.$props.value.claimsEndDate) {
                return this.$props.value.meritPresentationStartDate.isBefore(this.$props.value.meritPresentationEndDate) && this.$props.value.meritPresentationStartDate.isAfter(this.$props.value.endDate);
            }
            if (this.$props.value.claimsEndDate && this.$props.value.meritPresentationStartDate) {
                return this.$props.value.claimsEndDate.isBefore(this.$props.value.meritPresentationStartDate);
            }
            return true;
        },
        hasAttachMeritsDate() {
            return this.$props.value.meritPresentationStartDate && this.$props.value.meritPresentationEndDate;
        },
        isClaimPeriodInvalid() {
            if (this.$props.value.claimsStartDate && this.$props.value.claimsEndDate) {
                return !(this.$props.value.claimsStartDate.isBefore(this.$props.value.claimsEndDate));
            }
        },
        isClaimPeriodBeforeEndDate() {
            if (this.$props.value.endDate && this.$props.value.claimsStartDate) {
                return !(this.$props.value.endDate.isBefore(this.$props.value.claimsStartDate));
            }
        },
        addClaimDate() {
            return this.$props.value.startDate && this.$props.value.endDate;
        },
        isInscriptionPeriodInvalid() {
            if (this.$props.value.startDate && this.$props.value.endDate) {
                return !(this.$props.value.startDate.isBefore(this.$props.value.endDate));
            }
        },
        showCheckError() {
            return this.$props.submitted && !this.$props.value.turns.isValidTurnsChecked();
        },
        anyHasNoValue() {
            return !this.$props.value.bopDate && !this.$props.value.bopNumber && !this.$props.value.startDate && !this.$props.value.endDate;
        },
        processConfigurationGroupsOptions() {
            // Solo se ofrecen los activos, salvo el grupo actual del proceso aunque esté desactivado.
            const selectableGroups = this.$data.processConfigurationGroups.filter((group: ProcessConfigGroup) => group.active || group.id === (this as GeneralInfo).value.groupId);
            return selectableGroups.length > 0 ? _.orderBy(selectableGroups.map(process => ({
                id: process.id,
                name: process.title
            })), [processConfigurationGroup => _.deburr(processConfigurationGroup.name[(this as GeneralInfo).currentLanguage]).toLowerCase()]) : [];
        },
        personnelTypeOptions() {
            // Solo se ofrecen los activos que aplican a procesos, salvo el tipo actual del proceso aunque esté
            // oculto o ya no aplique a procesos (el catálogo completo lo resuelve).
            const personnelTypes: ProcessConfigPersonnelType[] = (this as GeneralInfo).processConfigPersonnelTypes || [];
            const currentPersonnelTypeId = (this as GeneralInfo).value.personnelTypeId;
            const selectable = personnelTypes.filter(personnelType => (personnelType.active && personnelType.appliesToProcesses) || personnelType.id === currentPersonnelTypeId);
            return _.orderBy(selectable.map(personnelType => ({
                id: personnelType.id,
                name: personnelType.title
            })), [option => _.deburr(option.name[(this as GeneralInfo).currentLanguage]).toLowerCase()]);
        },
        showAllowPostInscriptionMeritPresentation() {
            return this.$props.value.oppositionType != null && Number(this.$props.value.oppositionType) !== this.$data.oppositionType.enum.NOMINATION && Number(this.$props.value.oppositionType) !== this.$data.oppositionType.enum.FREE_DESIGNATION;
        }
    }
})

export default class GeneralInfo extends Vue {
    processConfigPersonnelTypes!: ProcessConfigPersonnelType[];
    @Prop() value!: Process;
    @Prop() submitted!: boolean;

    processType = ProcessType;
    processPhases = ProcessPhases;
    oppositionType = OppositionType;
    processStatus = ProcessStatus;
    Languages = Languages;
    Constants = Constants;

    currentLanguage!: string;
    yearsProcess: string[] = [];
    entityList: EntityOep[] = [];
    tabActive: string = '';
    processConfigurationGroups: ProcessConfigGroup[] = [];
    oppositionTypeTranslations = this.getOppositionTypeTranslations();

    onChangePostInscriptionMeritPresentation() {
        this.$emit('postInscriptionMeritPresentationToggleChange');
        if (!this.value.allowPostInscriptionMeritPresentation) {
            this.value.meritPresentationStartDate = null;
            this.value.meritPresentationEndDate = null;
        }
    }

    yearsToProcess() {
        const yearActual = moment().year();
        for (let i = Constants.process.YEAR_INITIAL; i <= yearActual + 1; i++) {
            this.yearsProcess.push(i.toString());
        }
    }

    getEntities() {
        EntityService.getEntityList()
            .then((response: EntityOep[]) =>
                this.entityList = response
            )
            .catch(error => console.log(error));
    }

    createVacanciesAttached() {
        this.value.vacanciesAttached = new VacanciesAttached({});
    }

    deleteVacanciesAttached() {
        this.value.vacanciesAttached = null;
    }

    get selectedGroupId() {
        return this.value.groupId;
    }

    onGroupChange(groupId: string) {
        const selectedGroup = this.$data.processConfigurationGroups.find((g: ProcessConfigGroup) => g.id === groupId);
        if (selectedGroup) {
            this.value.group = selectedGroup;
        }
    }

    getProcessConfigurationGroups() {
        MasterDataService.getProcessConfigGroupsCatalog()
            .then((response: any[]) => {
                this.processConfigurationGroups = response.map(processConfig =>
                    ProcessConfigFactory.create(ProcessConfigurationTypes.enum.GROUPSUBGROUP,
                        processConfig));
            }).catch(error => console.log(error));
    }

    getOppositionTypeTranslations() {
        if (this.value.type !== ProcessType.enum.POSTPROVISION) {
            const translations = _.cloneDeep(OppositionType.translations);
            delete translations[OppositionType.enum.FREE_DESIGNATION];
            return translations;
        }
        return OppositionType.translations;
    }

    mounted() {
        this.tabActive = this.currentLanguage;
        this.getProcessConfigurationGroups();
        this.getEntities();
        this.yearsToProcess();
    }
}
